/**
 * NEXORAX DIGITAL NEURAL CORE - REVENUE INSIGHTS D3 ENGINE (NODE-07.5)
 * In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
 * Visual NexoraX database model connecting to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider)
 */

import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Cpu, RefreshCw, Layers, ShieldCheck, Database, Landmark, Percent } from "lucide-react";

interface TaxEntry {
  id: string;
  date: string;
  grossHarvest: number;
  NexoraXLevy: number;
  charity1: number;
  charity2: number;
  charity3: number;
  lifetimeTax: number;
  netSettlement: number;
  SecurePaymentBankRoute: string;
  gdriveMirrorStatus: string;
}

export function RevenueD3Chart() {
  const [data, setData] = useState<TaxEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMetric, setActiveMetric] = useState<"both" | "gross" | "net">("both");
  const [hoveredBar, setHoveredBar] = useState<{
    date: string;
    gross: number;
    net: number;
    tax: number;
    levy: number;
    x: number;
    y: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 350 });

  // 1. Fetch Tax Ledger from Backend API Route
  const fetchLedger = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/tax/ledger");
      if (res.ok) {
        const payload = await res.json();
        // Sort chronologically (oldest to newest) for chart timeline representation
        const sorted = (payload.ledger || []).slice().reverse();
        setData(sorted);
      } else {
        throw new Error("HTTP error on billing data ingress.");
      }
    } catch (err: any) {
      console.error("[D3 SYSTEM ERROR]", err);
      setError("Failed to coordinate secure connection to accounting databases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, []);

  // 2. Responsive Container Resize Observer logic
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const { width } = entries[0].contentRect;
      // Maintain proper aspect ratio bounds
      const adjustedWidth = Math.max(280, width);
      setDimensions({
        width: adjustedWidth,
        height: 350
      });
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // 3. Render D3 Chart on width/height or data shift
  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    // Clear previous SVG content rendering
    const svgElement = d3.select(svgRef.current);
    svgElement.selectAll("*").remove();

    const margin = { top: 30, right: 20, bottom: 40, left: 60 };
    const chartWidth = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;

    const g = svgElement
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X-Scale: Dates
    const x0 = d3.scaleBand()
      .domain(data.map((d: TaxEntry) => d.date))
      .rangeRound([0, chartWidth])
      .paddingInner(0.2);

    // Sub-X-Scale for Grouped Columns
    const keys = activeMetric === "both" ? ["grossHarvest", "netSettlement"] : 
                 activeMetric === "gross" ? ["grossHarvest"] : ["netSettlement"];
    
    const x1 = d3.scaleBand()
      .domain(keys)
      .rangeRound([0, x0.bandwidth()])
      .padding(0.05);

    // Y-Scale: Amount Limit
    const maxVal = d3.max(data, (d: TaxEntry) => Math.max(d.grossHarvest, d.netSettlement)) || 1000000;
    const y = d3.scaleLinear()
      .domain([0, maxVal * 1.1]) // Add 10% whitespace overhead
      .rangeRound([chartHeight, 0]);

    // Color pairing configurations
    const colorMap: Record<string, string> = {
      grossHarvest: "#06b6d4", // Cyber Cyan
      netSettlement: "#10b981", // Emerald Green
    };

    // Grid lines Setup
    g.append("g")
      .attr("class", "grid")
      .attr("opacity", 0.1)
      .call(
        d3.axisLeft(y)
          .tickSize(-chartWidth)
          .tickFormat("" as any)
      )
      .selectAll("line")
      .attr("stroke", "#475569");

    // X-Axis render
    g.append("g")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(d3.axisBottom(x0).tickSizeOuter(0))
      .call(g => g.select(".domain").attr("stroke", "#334155"))
      .call(g => g.selectAll(".tick text")
        .attr("fill", "#94a3b8")
        .attr("font-family", "JetBrains Mono, SFMono-Regular, monospace")
        .attr("font-size", "8.5px")
      );

    // Y-Axis render
    g.append("g")
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickFormat(d => `£${(d as number) >= 1000000 ? `${((d as number)/1000000).toFixed(1)}M` : `${((d as number)/1000).toFixed(0)}K`}`)
      )
      .call(g => g.select(".domain").attr("stroke", "#334155"))
      .call(g => g.selectAll(".tick text")
        .attr("fill", "#94a3b8")
        .attr("font-family", "JetBrains Mono, SFMono-Regular, monospace")
        .attr("font-size", "8.5px")
      );

    // Bars grouping draw
    const barGroups = g.append("g")
      .selectAll("g")
      .data(data)
      .join("g")
      .attr("transform", (d: any) => `translate(${x0(d.date) || 0},0)`);

    barGroups.selectAll("rect")
      .data((d: any) => keys.map(key => ({ key, value: (d as any)[key], rawEntry: d })))
      .join("rect")
      .attr("x", (d: any) => x1(d.key)!)
      .attr("y", chartHeight) // Initial animate baseline
      .attr("width", x1.bandwidth())
      .attr("height", 0)
      .attr("fill", (d: any) => colorMap[d.key])
      .attr("rx", 3)
      .attr("class", "cursor-pointer transition-all duration-300 hover:brightness-125")
      .on("mouseenter", function (event, d: any) {
        const [mx, my] = d3.pointer(event, svgRef.current);
        setHoveredBar({
          date: d.rawEntry.date,
          gross: d.rawEntry.grossHarvest,
          net: d.rawEntry.netSettlement,
          tax: d.rawEntry.lifetimeTax,
          levy: d.rawEntry.NexoraXLevy,
          x: mx + 15,
          y: my - 100
        });
      })
      .on("mousemove", function (event) {
        const [mx, my] = d3.pointer(event, svgRef.current);
        setHoveredBar(prev => prev ? { ...prev, x: mx + 15, y: my - 100 } : null);
      })
      .on("mouseleave", function () {
        setHoveredBar(null);
      })
      // Interactive Transition triggers
      .transition()
      .duration(800)
      .delay((_, idx) => idx * 55)
      .attr("y", (d: any) => y(d.value))
      .attr("height", (d: any) => chartHeight - y(d.value));

  }, [data, dimensions, activeMetric]);

  return (
    <div className="bg-black/40 border border-slate-800 rounded-xl p-6 shadow-xl flex flex-col gap-6 text-left relative overflow-hidden">
      {/* Decorative Gradient Flare */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

      {/* Preamble */}
      <div className="bg-amber-500/5 border border-amber-500/25 p-2 rounded-lg text-center">
        <p className="text-[9.5px] text-amber-500 font-mono italic">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 pb-4 border-b border-slate-800/80">
        <div>
          <div className="text-cyan-500 font-bold text-[9px] uppercase tracking-[0.25em] flex items-center gap-1.5 leading-normal">
            <Landmark className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
            Secure Payment Provider NETWORK DIRECTIVE // NODE-07 ENGINE
          </div>
          <h3 className="text-white text-md font-sans font-black uppercase tracking-wider mt-1">
            REVENUE INSIGHTS &amp; REAL-TIME SETTLEMENT MATRIX
          </h3>
          <p className="text-[10px] text-slate-400 font-mono mt-0.5 uppercase tracking-wide">
            AUTOMATED REPRODUCTIVE D3 OVERSIGHT OF GROSS HARVESTS VS SECURED finance REVISIONS
          </p>
        </div>

        {/* Tab / Toggle Controls */}
        <div className="flex gap-1 bg-[#050914] border border-slate-800 p-1.5 rounded-lg font-mono text-[9px]">
          <button
            onClick={() => setActiveMetric("both")}
            className={`px-3 py-1.5 uppercase font-extrabold rounded-md cursor-pointer transition-colors ${activeMetric === "both" ? "bg-cyan-950 text-cyan-400 border border-cyan-500/30" : "text-slate-500 hover:text-slate-300"}`}
          >
            Show Both
          </button>
          <button
            onClick={() => setActiveMetric("gross")}
            className={`px-3 py-1.5 uppercase font-extrabold rounded-md cursor-pointer transition-colors ${activeMetric === "gross" ? "bg-cyan-950 text-cyan-400 border border-cyan-500/30" : "text-slate-500 hover:text-slate-300"}`}
          >
            Gross Harvest
          </button>
          <button
            onClick={() => setActiveMetric("net")}
            className={`px-3 py-1.5 uppercase font-extrabold rounded-md cursor-pointer transition-colors ${activeMetric === "net" ? "bg-emerald-950 text-emerald-400 border border-emerald-500/20" : "text-slate-500 hover:text-slate-300"}`}
          >
            Net Settlements
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 font-mono text-[10px] text-slate-400">
          <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
          ESTABLISHING DATA COURIER INTEGRITY PROTOCOLS...
        </div>
      ) : error ? (
        <div className="p-4 bg-rose-950/20 border border-rose-500/30 text-rose-400 font-mono text-[10px] rounded-lg">
          CRITICAL COVENANT FAULT: {error}
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-slate-500 font-mono text-[10px] uppercase">
          [LEDGER EMPTY] Submit gross metrics in the core panel to generate insights telemetry.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
          
          {/* Main D3 Canvas Column */}
          <div className="xl:col-span-2 bg-[#02050c]/80 border border-slate-900 rounded-xl p-4 relative" ref={containerRef}>
            
            {/* Visual D3 Canvas */}
            <svg 
              ref={svgRef} 
              width={dimensions.width} 
              height={dimensions.height} 
              className="overflow-visible select-none"
            />

            {/* Hover Tooltip Render */}
            {hoveredBar && (
              <div 
                className="absolute z-10 p-3 bg-[#010803] border-2 border-emerald-500/80 rounded-lg shadow-2xl font-mono text-[9px] w-52 pointer-events-none"
                style={{ left: `${hoveredBar.x}px`, top: `${hoveredBar.y}px` }}
              >
                <div className="text-slate-500 text-[8px] font-bold uppercase tracking-wider pb-1 border-b border-emerald-950/40">Timestamp {hoveredBar.date}</div>
                <div className="space-y-1.5 mt-2">
                  <div className="flex justify-between">
                    <span className="text-cyan-400 uppercase font-black text-[8px]">Daily Gross:</span>
                    <span className="text-white font-extrabold">£{hoveredBar.gross.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-amber-500">
                    <span className="text-[8px]">1% 3% Community Allocation:</span>
                    <span className="font-bold">£{hoveredBar.levy.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-violet-400">
                    <span className="text-[8px]">10% Maintenance & Hosting Fee:</span>
                    <span className="font-bold">£{hoveredBar.tax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-emerald-950/20 px-1 py-0.5 rounded text-emerald-400 mt-1 border-t border-emerald-900/30">
                    <span className="uppercase font-black text-[8.5px]">Net Ingress:</span>
                    <span className="font-black">£{hoveredBar.net.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-6 mt-3 font-mono text-[9px] border-t border-slate-900 pt-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-cyan-500 block"></span>
                <span className="text-slate-400 uppercase">Gross Harvest (Tax Base)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-emerald-500 block"></span>
                <span className="text-slate-400 uppercase">Net Settled to SecurePayment UK</span>
              </div>
            </div>
          </div>

          {/* Binding Manifest Side Column */}
          <div className="flex flex-col gap-4">
            
            {/* Visual Settlement Route Tracker */}
            <div className="bg-[#030912] border border-slate-900 p-4 rounded-xl flex flex-col gap-3 h-full">
              <span className="text-[8px] text-slate-500 font-mono font-black uppercase tracking-widest border-b border-slate-800/80 pb-1.5 block">
                SecurePayment Ingress Settlement Route
              </span>

              <div className="space-y-4 flex-1">
                {/* Visual Pipeline nodes */}
                <div className="flex flex-col gap-3 relative">
                  
                  <div className="flex items-start gap-2.5 bg-slate-950/40 p-2.5 rounded border border-slate-900/80">
                    <div className="p-1 bg-cyan-500/10 rounded border border-cyan-500/35 text-cyan-400 mt-0.5">
                      <Cpu className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-[8.5px] text-white font-extrabold uppercase">1. Gross Revenue Triage</div>
                      <div className="text-[8px] text-slate-500 font-mono uppercase mt-0.5">Metrics parsed on NexoraX Node-07</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-slate-950/40 p-2.5 rounded border border-slate-900/80">
                    <div className="p-1 bg-amber-500/10 rounded border border-amber-500/35 text-amber-500 mt-0.5">
                      <Percent className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-[8.5px] text-white font-extrabold uppercase">2. Multi-Tiered Levy Extract</div>
                      <div className="text-[8px] text-slate-500 font-mono uppercase mt-0.5">1% 3% Community Allocation &bull; 10% Maintenance & Hosting Fee</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-slate-950/40 p-2.5 rounded border border-emerald-950/60 text-left">
                    <div className="p-1 bg-emerald-500/10 rounded border border-emerald-500/35 text-emerald-400 mt-0.5">
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-[8.5px] text-emerald-400 font-black uppercase">3. SecurePayment Ingress Settlement</div>
                      <div className="text-[8px] text-slate-400 font-mono mt-0.5">Sort: <span className="text-white font-bold">Verified Payment Configuration</span> | Account: <span className="text-white font-bold">Secure Billing Provider</span></div>
                    </div>
                  </div>

                </div>
              </div>

              <div className="p-2 bg-[#020d06] border border-emerald-500/20 rounded">
                <span className="text-[7.5px] text-emerald-500 block uppercase font-bold tracking-widest">
                  Automatic Routing Confirmation
                </span>
                <p className="text-[8.5px] text-slate-300 font-mono mt-1">
                  Reconciliation audit completed. Daily net balances are bound, secured, and transmitted to <strong>Secure Payment Provider, London, UK</strong>. No secondary routes.
                </p>
              </div>
            </div>

          </div>

        </div>
      )}

      {/* Audit Sync Confirmation bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-black/60 border border-slate-900 px-4 py-3 rounded-lg font-mono text-[8.5px] text-slate-500 gap-2 uppercase">
        <div className="flex items-center gap-1.5 text-slate-400">
          <Database className="w-3.5 h-3.5 text-cyan-500" />
          <span>Continuous Ledger Synced &bull; API Route: /api/tax/ledger</span>
        </div>
        <div className="flex items-center gap-1.5 text-emerald-500 font-bold">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>CloudSync-02 Verified Replication Pass Completed</span>
        </div>
      </div>
    </div>
  );
}
